import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const REASONS = [
  { v: "eingang", l: "Wareneingang" },
  { v: "verbrauch", l: "Verbrauch" },
  { v: "verkauf", l: "Verkauf" },
  { v: "ausschuss", l: "Ausschuss" },
  { v: "inventur", l: "Inventur" },
];

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [productId, setProductId] = useState("");
  const [delta, setDelta] = useState(1);
  const [reason, setReason] = useState("eingang");

  const load = async () => {
    const [p, m] = await Promise.all([api.get("/products"), api.get("/inventory/movements")]);
    setProducts(p.data); setMovements(m.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!productId) return toast.error("Produkt wählen");
    try {
      await api.post("/inventory/adjust", { product_id: productId, delta: parseInt(delta || 0), reason });
      toast.success("Bewegung gespeichert");
      setDelta(1); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const low = products.filter((p) => (p.stock ?? 0) <= (p.min_stock ?? 0));

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Bestände</div>
        <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl">Lagerbestand</h1>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="card-lux overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[540px]">
            <thead className="bg-[#0A0A0A] border-b border-[#1A1A1A] text-[#A8A398] text-xs uppercase tracking-widest">
              <tr><th className="text-left p-3">Produkt</th><th className="text-left p-3">Kategorie</th><th className="text-right p-3">Bestand</th><th className="text-right p-3">Min</th></tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className={`border-b border-[#141414] ${(p.stock ?? 0) <= (p.min_stock ?? 0) ? "bg-[#5B1319]/15" : ""}`}>
                  <td className="p-3">{p.name}</td>
                  <td className="p-3 text-[#A8A398]">{p.category}</td>
                  <td className="p-3 text-right font-mono">{p.stock}</td>
                  <td className="p-3 text-right font-mono text-[#A8A398]">{p.min_stock}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
        <aside className="space-y-4">
          <div className="card-lux p-4">
            <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3">Bestand anpassen</div>
            <select value={productId} onChange={(e) => setProductId(e.target.value)} className="w-full bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 mb-2 outline-none focus:border-[#D4AF37]">
              <option value="">— Produkt —</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.stock})</option>)}
            </select>
            <div className="flex gap-2 mb-2">
              <input type="number" value={delta} onChange={(e) => setDelta(e.target.value)} className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none" />
              <select value={reason} onChange={(e) => setReason(e.target.value)} className="bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none">
                {REASONS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
              </select>
            </div>
            <button data-testid="inventory-adjust-btn" onClick={submit} className="w-full bg-[#D4AF37] text-black text-sm uppercase tracking-widest py-2 rounded hover:bg-[#EEDC82]">Buchen</button>
          </div>
          <div className="card-lux p-4">
            <div className="text-xs uppercase tracking-widest text-[#EE9CA1] mb-3">Warnungen</div>
            {low.length === 0 ? <div className="text-[#A8A398] text-sm">Alles gut.</div> :
              low.map((p) => <div key={p.id} className="text-sm py-1 flex justify-between border-b border-[#1A1A1A]"><span>{p.name}</span><span className="font-mono">{p.stock}</span></div>)}
          </div>
        </aside>
      </div>

      <div className="card-lux overflow-hidden">
        <div className="px-4 py-3 border-b border-[#1A1A1A] font-heading text-xl">Verlauf</div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-[#0A0A0A] border-b border-[#1A1A1A] text-[#A8A398] text-xs uppercase tracking-widest">
            <tr><th className="text-left p-3">Zeit</th><th className="text-left p-3">Produkt</th><th className="text-left p-3">Grund</th><th className="text-right p-3">Δ</th></tr>
          </thead>
          <tbody>
            {movements.slice(0, 100).map((m) => {
              const p = products.find((x) => x.id === m.product_id);
              return (
                <tr key={m.id} className="border-b border-[#141414]">
                  <td className="p-3 font-mono text-xs">{m.at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="p-3">{p?.name || m.product_id}</td>
                  <td className="p-3 text-[#A8A398]">{m.reason}</td>
                  <td className={`p-3 text-right font-mono ${m.delta < 0 ? "text-[#EE9CA1]" : "text-[#9BE0A5]"}`}>{m.delta > 0 ? "+" : ""}{m.delta}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
